import { useState, useEffect } from 'react';
import { Package, Truck, Loader2, FileText, Plus, ExternalLink, RefreshCw, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = '';

const STATUS_COLORS = {
  en_attente:      'bg-yellow-100 text-yellow-700',
  en_preparation:  'bg-blue-100 text-blue-700',
  expediee:        'bg-indigo-100 text-indigo-700',
  au_bureau:       'bg-purple-100 text-purple-700',
  livree:          'bg-green-100 text-green-700',
  echec_livraison: 'bg-red-100 text-red-700',
  retournee:       'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  en_attente: 'En attente',
  en_preparation: 'Pris en charge',
  expediee: 'En transit',
  au_bureau: 'Au bureau',
  livree: 'Livre',
  echec_livraison: 'Echec',
  retournee: 'Retourne',
};

export default function AdminShipping() {
  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [search, setSearch] = useState('');

  const fetchOrders = async () => {
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/shipping?limit=100`, { credentials: 'include' });
      const data = await res.json();
      setOrders(data.orders || []);
      setTotal(data.total || 0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOrders(); }, []);

  const handleCreateParcel = async (orderId) => {
    setCreating(orderId);
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/shipping/${orderId}/create-parcel`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Colis cree ! Tracking: ${data.tracking}`);
        fetchOrders();
      } else {
        toast.error(data.detail || 'Erreur creation colis');
      }
    } catch {
      toast.error('Erreur de connexion');
    } finally {
      setCreating(null);
    }
  };

  const handleGetLabel = async (orderId) => {
    try {
      const res = await fetch(`${API_URL}/api/bloom-gestion-secure/shipping/${orderId}/label`, { credentials: 'include' });
      const data = await res.json();
      if (res.ok && data.label_url) {
        window.open(data.label_url, '_blank');
      } else {
        toast.error(data.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const filtered = orders.filter(o => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (
      o.order_id?.toLowerCase().includes(s) ||
      o.customer_name?.toLowerCase().includes(s) ||
      o.customer_phone?.includes(s) ||
      o.ecotrack_tracking?.toLowerCase().includes(s) ||
      o.wilaya?.toLowerCase().includes(s)
    );
  });

  const stats = {
    total: orders.length,
    withParcel: orders.filter(o => o.ecotrack_tracking).length,
    pending: orders.filter(o => !o.ecotrack_tracking).length,
    delivered: orders.filter(o => o.order_status === 'livree').length,
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin" size={24} /></div>;

  return (
    <div data-testid="admin-shipping-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-serif font-bold" data-testid="shipping-title">Livraisons World Express</h1>
          <p className="text-sm text-[#6B6A68] mt-1">{total} commandes</p>
        </div>
        <button onClick={() => { setLoading(true); fetchOrders(); }} className="flex items-center gap-2 text-sm text-[#6B6A68] hover:text-[#1A1514]" data-testid="refresh-shipping">
          <RefreshCw size={16} /> Actualiser
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="bg-white border border-[#E0DFDD] p-4"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-[#6B6A68]">Total commandes</p></div>
        <div className="bg-white border border-[#E0DFDD] p-4"><p className="text-2xl font-bold text-blue-600">{stats.withParcel}</p><p className="text-xs text-[#6B6A68]">Colis crees</p></div>
        <div className="bg-white border border-[#E0DFDD] p-4"><p className="text-2xl font-bold text-yellow-600">{stats.pending}</p><p className="text-xs text-[#6B6A68]">En attente colis</p></div>
        <div className="bg-white border border-[#E0DFDD] p-4"><p className="text-2xl font-bold text-green-600">{stats.delivered}</p><p className="text-xs text-[#6B6A68]">Livrees</p></div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6A68]" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher par commande, nom, tel, tracking..." className="w-full border border-[#E0DFDD] pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#1A1514]" data-testid="shipping-search" />
        </div>
      </div>

      {/* Orders table */}
      <div className="bg-white border border-[#E0DFDD] overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E0DFDD] bg-[#F9F8F6]">
              <th className="text-left p-3 font-medium">Commande</th>
              <th className="text-left p-3 font-medium">Cliente</th>
              <th className="text-left p-3 font-medium">Wilaya</th>
              <th className="text-left p-3 font-medium">Tracking World Express</th>
              <th className="text-left p-3 font-medium">Statut</th>
              <th className="text-right p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-[#6B6A68]">Aucune commande trouvee</td></tr>
            ) : (
              filtered.map(o => (
                <tr key={o.order_id} className="border-b border-[#E0DFDD] last:border-0 hover:bg-[#F9F8F6]" data-testid={`shipping-row-${o.order_id}`}>
                  <td className="p-3">
                    <span className="font-mono text-xs font-bold">{o.order_id}</span>
                    <br /><span className="text-[10px] text-[#9B9A98]">{o.created_at?.split('T')[0]}</span>
                  </td>
                  <td className="p-3">
                    <span className="text-xs">{o.customer_name}</span>
                    <br /><span className="text-[10px] text-[#6B6A68]">{o.customer_phone}</span>
                  </td>
                  <td className="p-3 text-xs">{o.wilaya}<br /><span className="text-[10px] text-[#6B6A68]">{o.delivery_type === 'bureau' ? 'Bureau' : 'Domicile'}</span></td>
                  <td className="p-3">
                    {o.ecotrack_tracking ? (
                      <a href={`https://suivi.ecotrack.dz/suivi/${o.ecotrack_tracking}`} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-blue-600 hover:underline flex items-center gap-1" data-testid={`tracking-link-${o.order_id}`}>
                        {o.ecotrack_tracking.slice(-10)} <ExternalLink size={10} />
                      </a>
                    ) : (
                      <span className="text-xs text-[#9B9A98]">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={cn('px-2 py-0.5 text-[10px] font-medium rounded-full', STATUS_COLORS[o.order_status] || 'bg-gray-100 text-gray-600')}>
                      {STATUS_LABELS[o.order_status] || o.order_status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {!o.ecotrack_tracking ? (
                        <button
                          onClick={() => handleCreateParcel(o.order_id)}
                          disabled={creating === o.order_id}
                          className="flex items-center gap-1 bg-[#1A1514] text-white px-2.5 py-1.5 text-[10px] font-medium hover:bg-[#2a2524] disabled:opacity-50"
                          data-testid={`create-parcel-${o.order_id}`}
                        >
                          {creating === o.order_id ? <Loader2 size={10} className="animate-spin" /> : <Plus size={10} />}
                          Creer colis
                        </button>
                      ) : (
                        <button
                          onClick={() => handleGetLabel(o.order_id)}
                          className="flex items-center gap-1 bg-[#D4AF37] text-white px-2.5 py-1.5 text-[10px] font-medium hover:bg-[#c4a030]"
                          data-testid={`label-${o.order_id}`}
                        >
                          <FileText size={10} /> Bon PDF
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
